import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, X, ZoomIn } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { Reveal } from "../hooks/useScrollReveal";
import BackToTop from "./BackToTop";

const IMAGES = [
  "https://customer-assets.emergentagent.com/job_tervalia-atelier/artifacts/m6zgfn1l_ukazka_prace_1.png",
  "https://customer-assets.emergentagent.com/job_tervalia-atelier/artifacts/37hpnnnr_ukazka_prace_4.png",
  "https://customer-assets.emergentagent.com/job_tervalia-atelier/artifacts/mrcqn824_ukazka_prace_5.png",
  "https://customer-assets.emergentagent.com/job_tervalia-atelier/artifacts/h0atv326_ukazka_prace_6.png",
  "https://customer-assets.emergentagent.com/job_tervalia-atelier/artifacts/7afgfpg1_ukazka_prace_7.png",
];

// Each project opens with its current cover image, followed by detail photos.
const GALLERY_IMAGES = [
  [IMAGES[0], IMAGES[0], IMAGES[0]],
  [
    IMAGES[1],
    "/gallery/vyrezavany-detail1.png",
    "/gallery/vyrezavany-detail2.png",
  ],
  [
    IMAGES[2],
    "/gallery/surovy-beton-detail1.png",
    "/gallery/surovy-beton-detail2.png",
  ],
  [
    IMAGES[3],
    "/gallery/kamenna-textura-detail1.png",
    "/gallery/kamenna-textura-detail2.png",
  ],
  [
    IMAGES[4],
    "/gallery/strukturovany-relief-detail1.png",
    "/gallery/strukturovany-relief-detail2.png",
  ],
];

// On desktop (lg) we use a 6-col grid where every card spans 2 cols.
// Top row: items 1,2,3 (cols 1-2, 3-4, 5-6)
// Bottom row: items 4,5 centered → col-start-2 and col-start-4.
export default function Gallery() {
  const { t } = useI18n();
  const items = t.gallery.items;
  const [lightbox, setLightbox] = useState(null);

  const closeLightbox = () => setLightbox(null);
  const showPrevious = () =>
    setLightbox((current) => ({
      ...current,
      imageIndex:
        (current.imageIndex - 1 + GALLERY_IMAGES[current.projectIndex].length) %
        GALLERY_IMAGES[current.projectIndex].length,
    }));
  const showNext = () =>
    setLightbox((current) => ({
      ...current,
      imageIndex:
        (current.imageIndex + 1) % GALLERY_IMAGES[current.projectIndex].length,
    }));

  useEffect(() => {
    if (!lightbox) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") setLightbox(null);
      if (event.key === "ArrowLeft") {
        setLightbox((current) => ({
          ...current,
          imageIndex:
            (current.imageIndex - 1 + GALLERY_IMAGES[current.projectIndex].length) %
            GALLERY_IMAGES[current.projectIndex].length,
        }));
      }
      if (event.key === "ArrowRight") {
        setLightbox((current) => ({
          ...current,
          imageIndex:
            (current.imageIndex + 1) % GALLERY_IMAGES[current.projectIndex].length,
        }));
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [lightbox]);

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="bg-[#f9f8f5] pt-24 md:pt-36 pb-14 md:pb-20"
    >
      <div className="max-w-[1280px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-8 items-end mb-14">
          <Reveal className="md:col-span-7">
            <div className="eyebrow mb-6" data-testid="gallery-eyebrow">
              {t.gallery.eyebrow}
            </div>
            <h2
              data-testid="gallery-title"
              className="font-serif-hero text-[#252525] text-[34px] sm:text-[44px] md:text-[54px] leading-[1.08]"
            >
              {t.gallery.title}
            </h2>
          </Reveal>
          <Reveal className="md:col-span-5">
            <p className="text-[14.5px] leading-[1.75] text-[#4a4a45] max-w-[420px]">
              {t.gallery.subtitle}
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-6 md:gap-8">
          {items.map((item, i) => {
            // Every card spans 2 cols on desktop → 3 per row = 6 cols total.
            // Bottom-row items (index 3 and 4) get explicit col-start to center them.
            let placement = "lg:col-span-2";
            if (i === 3) placement += " lg:col-start-2";
            if (i === 4) placement += " lg:col-start-4";

            // Also center the last item on tablet if items count is odd
            let tabletPlacement = "";
            if (i === items.length - 1 && items.length % 2 === 1) {
              tabletPlacement = "sm:col-start-1 md:col-start-1 sm:max-md:col-span-2";
            }

            return (
              <Reveal key={item.code} className={`${placement} ${tabletPlacement}`}>
                <figure
                  className="tv-gallery-item"
                  data-testid={`gallery-item-${i + 1}`}
                >
                  <button
                    type="button"
                    onClick={() => setLightbox({ projectIndex: i, imageIndex: 0 })}
                    className="group relative block w-full aspect-[4/5] overflow-hidden cursor-zoom-in"
                    aria-label={`Otevřít galerii: ${item.title}`}
                  >
                    <img
                      src={IMAGES[i]}
                      alt={item.title}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.025]"
                    />
                    <span className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
                    <span className="absolute top-4 right-4 grid place-items-center w-10 h-10 rounded-full bg-[#292724]/80 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <ZoomIn size={18} strokeWidth={1.6} />
                    </span>
                  </button>
                  <figcaption className="mt-5 flex items-start justify-between gap-3">
                    <div>
                      <div className="font-serif text-[19px] md:text-[20px] text-[#252525] leading-tight">
                        {item.title}
                      </div>
                      <div className="mt-2 text-[10.5px] tracking-[0.2em] uppercase text-[#6f6f6a]">
                        {item.meta}
                      </div>
                    </div>
                    <div className="text-[10px] tracking-[0.24em] uppercase text-[#b86a55] pt-1 shrink-0">
                      {item.code}
                    </div>
                  </figcaption>
                </figure>
              </Reveal>
            );
          })}
        </div>

        <div className="mt-8 md:mt-10">
          <BackToTop testId="back-to-top-gallery" />
        </div>
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-[100] bg-[#111]/95 flex items-center justify-center"
          role="dialog"
          aria-modal="true"
          aria-label={items[lightbox.projectIndex].title}
          onClick={closeLightbox}
        >
          <button
            type="button"
            onClick={closeLightbox}
            className="absolute top-4 right-4 md:top-8 md:right-8 z-20 grid place-items-center w-12 h-12 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            aria-label="Zavřít galerii"
          >
            <X size={25} strokeWidth={1.5} />
          </button>

          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              showPrevious();
            }}
            className="absolute left-3 md:left-8 z-20 grid place-items-center w-12 h-12 rounded-full bg-white/10 text-white hover:bg-[#b86a55] transition-colors"
            aria-label="Předchozí fotografie"
          >
            <ChevronLeft size={28} strokeWidth={1.5} />
          </button>

          <div
            className="w-full h-full px-16 md:px-24 py-20 flex flex-col items-center justify-center"
            onClick={(event) => event.stopPropagation()}
          >
            <img
              src={GALLERY_IMAGES[lightbox.projectIndex][lightbox.imageIndex]}
              alt={items[lightbox.projectIndex].title}
              className="max-w-full max-h-[calc(100vh-10rem)] object-contain"
            />
            <div className="absolute bottom-5 md:bottom-8 left-6 right-6 flex items-end justify-between gap-6 text-white">
              <div className="font-serif text-xl md:text-2xl">
                {items[lightbox.projectIndex].title}
              </div>
              <div className="text-xs tracking-[0.2em] text-white/70">
                {lightbox.imageIndex + 1} / {GALLERY_IMAGES[lightbox.projectIndex].length}
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              showNext();
            }}
            className="absolute right-3 md:right-8 z-20 grid place-items-center w-12 h-12 rounded-full bg-white/10 text-white hover:bg-[#b86a55] transition-colors"
            aria-label="Další fotografie"
          >
            <ChevronRight size={28} strokeWidth={1.5} />
          </button>
        </div>
      )}
    </section>
  );
}
